import React, { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import menu from '../Data/Menu/menu.json';
import { Toaster } from 'react-hot-toast';
import axiosInstance from "../../utils/axiosInstance";
import toast from 'react-hot-toast';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from "@mui/material";
import usePermissions from "../../hooks/usePermissions";


function Sidebar() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const isSidebarMini = useSelector((state) => state.Sidebar.isSidebarMini);

    const [menuData, setMenuData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeKey, setActiveKey] = useState(location.pathname);
    const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

    const { hasPermission, user } = usePermissions();

    // Map menu keys to backend permission modules. Backend: dashboard, profile, role, permission, user, podcaster, category, subCategory, podcast, episode, settings
    const hasAnyModule = (perms, modules) => modules.some((m) => perms[m]);
    const getAllowedMenu = () => {
        const fullMenu = menu.menu || [];
        if (!user) return [];
        let roleName = "";
        if (user.role) {
            if (typeof user.role === 'string') roleName = user.role;
            else if (user.role.name) roleName = user.role.name;
            else if (user.role.roleName) roleName = user.role.roleName;
            else if (user.role.title) roleName = user.role.title;
        }
        if (!roleName && user.roleName) roleName = user.roleName;

        const normalizedRole = roleName ? String(roleName).toLowerCase().replace(/[^a-z]/g, '') : '';
        const isSuperAdmin =
            Number(user.roleId) === 1 ||
            normalizedRole === 'superadmin' ||
            normalizedRole === 'admin' ||
            normalizedRole === 'super' ||
            normalizedRole === 'root';
        if (isSuperAdmin) return fullMenu;
        const perms = user.permissions || {};
        
        if (Object.keys(perms).length === 0) return [];

        const menuToBackend = {
            'dashboard': ['dashboard'],
            'role-management': ['role', 'permission', 'user'],
            'customer-management': ['user'],
            'audioLibrary': ['audioLibrary'],
            'album': ['album'],
            'track': ['category', 'subCategory', 'podcaster', 'podcast', 'track'],
            'reports': [],
            'settings': ['settings'],
        };
        const childKeyToBackend = {
            'permission': ['permission'],
            'role': ['role'],
            'user': ['user'],
            'customer': ['user'],
            'categories': ['category'],
            'podcasters': ['podcaster'],
            'track': ['album', 'track'],
            'audioLibrary': ['audioLibrary'],
            'album': ['album'],
        };

        return fullMenu
            .filter((d) => {
                const backendModules = menuToBackend[d.key];
                if (!backendModules || backendModules.length === 0) return false;
                return hasAnyModule(perms, backendModules);
            })
            .map((d) => {
                if (!d.children || d.children.length === 0) return d;
                const filteredChildren = d.children.filter((ch) => {
                    const mods = childKeyToBackend[ch.key] || (perms[ch.key] ? [ch.key] : []);
                    return mods.length > 0 && hasAnyModule(perms, mods);
                });
                return { ...d, children: filteredChildren };
            });
    };

    useEffect(() => {
        setActiveKey(location.pathname);
    }, [location]);

    useEffect(() => {
        if (menuData.length === 0) return;
        const allSubMenus = document.getElementsByClassName("has-children");
        for (let i = 0; i < allSubMenus.length; i++) {
            allSubMenus[i].classList.remove("show");
            if (allSubMenus[i].parentElement) {
                allSubMenus[i].parentElement.setAttribute("aria-expanded", "false");
            }
        }

        let bestMatch = null;
        let maxLen = 0;

        const checkMatch = (route) => {
            if (location.pathname === route || location.pathname.startsWith(route + "/")) {
                if (route.length > maxLen) {
                    maxLen = route.length;
                    bestMatch = route;
                }
            }
        };

        menuData.forEach((d) => {
            if (d.children && d.children.length > 0) {
                d.children.forEach(child => checkMatch(process.env.PUBLIC_URL + "/" + child.routerLink[0]));
            } else if (d.routerLink && d.routerLink.length > 0) {
                checkMatch(process.env.PUBLIC_URL + "/" + d.routerLink[0]);
            }
        });

        const isRouteActive = (route) => route === bestMatch;

        menuData.forEach((d, i) => {
            if (d.children && d.children.length > 0) {
                const hasActiveChild = d.children.some(child =>
                    isRouteActive(process.env.PUBLIC_URL + "/" + child.routerLink[0])
                );
                if (hasActiveChild) {
                    const menutab = document.getElementById(`menu-Pages${i}`);
                    if (menutab) {
                        menutab.classList.add("show");
                        if (menutab.parentElement) {
                            menutab.parentElement.setAttribute("aria-expanded", "true");
                        }
                    }
                }
            }
        });
    }, [location.pathname, menuData]);

    useEffect(() => {
        setMenuData(getAllowedMenu());
        setLoading(false);
    }, [user]);

    const openChildren = (e, id) => {
        e.preventDefault();
        const menutab = document.getElementById(id);
        if (!menutab) return;
        const parentLi = menutab.parentElement;
        if (!parentLi) return;
        const isCurrentlyOpen = menutab.classList.contains("show");
        const otherTabs = document.getElementsByClassName("has-children");

        for (let i = 0; i < otherTabs.length; i++) {
            const otherTab = otherTabs[i];
            const otherParentLi = otherTab.parentElement;

            if (otherTab.id !== id) {
                otherTab.classList.remove("show");
                otherParentLi?.setAttribute("aria-expanded", "false");
            }
        }

        // Toggle current
        if (isCurrentlyOpen) {
            menutab.classList.remove("show");
            parentLi.setAttribute("aria-expanded", "false");
        } else {
            menutab.classList.add("show");
            parentLi.setAttribute("aria-expanded", "true");
        }
    };

    const handleLogoutClick = (e) => {
        setLogoutDialogOpen(true);
    };

    // Logout function
    const handleLogout = async () => {
        setLogoutDialogOpen(false); // Close dialog
        
        try {
            const response = await axiosInstance.post(
                '/api/admin/auth/logout'
            );

            toast.success(response.data?.message || 'Logged out successfully');

        } catch (err) {
            console.error('Logout error:', err);

            toast.error(
                err.response?.data?.message || 'Logout failed'
            );
        } finally {
            sessionStorage.clear();

            document.cookie = 'token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
            setTimeout(() => {
                window.location.href =
                    process.env.PUBLIC_URL + '/sign-in';
            }, 800);
        }
    };

    let bestMatch = null;
    let maxLen = 0;

    const checkMatch = (route) => {
        if (activeKey === route || activeKey.startsWith(route + "/")) {
            if (route.length > maxLen) {
                maxLen = route.length;
                bestMatch = route;
            }
        }
    };

    menuData.forEach((d) => {
        if (d.children && d.children.length > 0) {
            d.children.forEach(child => checkMatch(process.env.PUBLIC_URL + "/" + child.routerLink[0]));
        } else if (d.routerLink && d.routerLink.length > 0) {
            checkMatch(process.env.PUBLIC_URL + "/" + d.routerLink[0]);
        }
    });

    const isRouteActive = (route) => route === bestMatch;

    const isAnyChildActive = (item) => {
        if (!item.children) return false;
        return item.children.some(child => isRouteActive(process.env.PUBLIC_URL + "/" + child.routerLink[0]));
    };

    const handleClick = (route) => {
        setActiveKey(route);
        navigate(route);
        if (window.innerWidth < 992) {
            const sidebar = document.getElementById('mainsidemenu');
            if (sidebar) sidebar.classList.remove('open');
            const overlay = document.getElementById('sidebar-overlay');
            if (overlay) overlay.classList.remove('active');
        }
    };

    const menuTranslationMapping = {
        "Dashboard": "menues.dashboard",
        "Customer Management": "customer_management",
        "Role Management": "menues.role_management",
        "User Management":"menues.user_management",
        "Listeners": "menues.listeners",
        "Admistrators": "menues.system_users",
        "Podcasters": "menues.podcasters",
        "Audio Library": "menues.audio_library",
        "Category Management": "menues.category_management",
        "Audiobooks Category": "menues.audiobooks_category",
        "Podcasts Category": "menues.podcasts_category",
        "Report Management": "menues.report_management",
        "Settings": "menues.settings",
        "Privacy Policy": "menues.settings_privacy_policy",
        "Terms & Conditions": "menues.settings_terms_conditions",
        "About us": "menues.settings_about_us",
        "Contact Settings": "menues.settings_contact_settings",
        "Media Content Management": "menues.media_content_management",
        "Album List": "menues.albums",
        "Albums Scheduling & Publishing": "menues.albums_scheduling_publishing",
        "Tracks Scheduling & Publishing": "menues.tracks_scheduling_publishing",
        "Payments & Subscriptions": "menues.payment_subscriptions",
        "Subscription plans": "menues.subscription_plans",
        "Subscription History": "menues.subscription_history",
        "Transactions History": "menues.transactions_history",
        "Advertising": "menues.advertising",
        "Internal Audio Ads": "menues.internal_audio_ads",
        "VIP Banners": "menues.vip_banners",
        "Campaign Management": "menues.campaign_management",
        "Push Notifications": "menues.push_notifications",
        "Email Notifications": "menues.email_notifications",
        "Notification Campaigns": "menues.notification_campaigns",
        "Content Report Management": "menues.content_report_management",
        "Google AdMob Settings": "google_admob_settings",
    };

    if (loading) {
        return (
            <div
                className="position-fixed top-0 start-0 w-100 h-100 d-flex flex-column justify-content-center align-items-center"
                style={{ background: "linear-gradient(135deg, #f3f4f6 0%, #e0e7ff 100%)", zIndex: 9999 }}
            >
                <div className="spinner-border text-primary mb-3" role="status" style={{ width: "4rem", height: "4rem", borderWidth: "6px" }}></div>
                <h5 className="fw-semibold text-primary">{t('loading_dashboard', 'Loading your dashboard...')}</h5>
            </div>
        );
    }

    return (
        <>
            <Toaster position="top-center" reverseOrder={true} duration={10000} />
            <div id="mainsidemenu" className={`sidebar ${isSidebarMini ? "sidebar-mini" : ""}`} style={{ overflow: 'auto' }}>
                <div className="d-flex flex-column">
                    <div className="sidebar-logo">
                        {isSidebarMini ? (
                            <img src="/logo-small.svg" alt="logo" />
                        ) : (
                            <img src="/logo.svg" alt="logo" />
                        )}

                        <span 
                            className="mobile_sidebar_close"
                            onClick={() => {
                                const sidebar = document.getElementById('mainsidemenu');
                                const overlay = document.getElementById('sidebar-overlay');
                                if (sidebar) sidebar.classList.remove('open');
                                if (overlay) overlay.classList.remove('active');
                            }}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path d="M12 22.75C6.07 22.75 1.25 17.93 1.25 12C1.25 6.07 6.07 1.25 12 1.25C17.93 1.25 22.75 6.07 22.75 12C22.75 17.93 17.93 22.75 12 22.75ZM12 2.75C6.9 2.75 2.75 6.9 2.75 12C2.75 17.1 6.9 21.25 12 21.25C17.1 21.25 21.25 17.1 21.25 12C21.25 6.9 17.1 2.75 12 2.75Z" fill="white"/>
                                <path d="M9.16986 15.5801C8.97986 15.5801 8.78986 15.5101 8.63986 15.3601C8.34986 15.0701 8.34986 14.5901 8.63986 14.3001L14.2999 8.64011C14.5899 8.35011 15.0699 8.35011 15.3599 8.64011C15.6499 8.93011 15.6499 9.41011 15.3599 9.70011L9.69986 15.3601C9.55986 15.5101 9.35986 15.5801 9.16986 15.5801Z" fill="white"/>
                                <path d="M14.8299 15.5801C14.6399 15.5801 14.4499 15.5101 14.2999 15.3601L8.63986 9.70011C8.34986 9.41011 8.34986 8.93011 8.63986 8.64011C8.92986 8.35011 9.40986 8.35011 9.69986 8.64011L15.3599 14.3001C15.6499 14.5901 15.6499 15.0701 15.3599 15.3601C15.2099 15.5101 15.0199 15.5801 14.8299 15.5801Z" fill="white"/>
                            </svg>
                        </span>
                    </div>
                    <ul className="menu-list flex-grow-1 mb-0" style={{ overflow: 'unset' }}>
                        {menuData?.map((d, i) => {
                            if (d.children?.length === 0) {                                
                                const route = d.routerLink?.[0] ?? null;
                                if(route === null) return;
                                
                                return (
                                    <li
                                        key={`menu-item-${i}`}
                                        className={`collapsed ${
                                            isRouteActive(process.env.PUBLIC_URL + "/" + route) ? "active" : ""
                                        }`}
                                    >
                                        <Link
                                            to={process.env.PUBLIC_URL + "/" + d.routerLink[0]}
                                            className={`m-link`}
                                            onClick={() => handleClick(process.env.PUBLIC_URL + "/" + d.routerLink[0])}
                                        >
                                            <span dangerouslySetInnerHTML={{ __html: d.iconClass }} className="sidebar_icon" />
                                            <span>{t(menuTranslationMapping[d.name] || d.name, d.name)}</span>
                                        </Link>
                                    </li>
                                );
                            }
                            return (
                                <li key={`menu-item-${i}`} className={`collapsed ${isAnyChildActive(d) ? "active" : ""}`}>
                                    <a
                                        className={`m-link`}
                                        href="#!"
                                        onClick={(e) => {
                                            openChildren(e, `menu-Pages${i}`);
                                            if (d.children && d.children.length > 0) {
                                                handleClick(process.env.PUBLIC_URL + "/" + d.children[0].routerLink[0]);
                                            }
                                        }}
                                    >
                                        <span dangerouslySetInnerHTML={{ __html: d.iconClass }} className="sidebar_icon" />
                                        <span>{t(menuTranslationMapping[d.name] || d.name, d.name)}</span>
                                        <span className="arrow icofont-rounded-right ms-auto"></span>
                                    </a>
                                    {d.children.length > 0 &&
                                        <ul className="sub-menu collapse has-children" id={`menu-Pages${i}`}>
                                            {d.children.map((data, ind) => {
                                                return (
                                                    <li key={`submenu-item-${ind}`}>
                                                        <Link
                                                            className={`${isRouteActive(process.env.PUBLIC_URL + "/" + data.routerLink[0]) ? "ms-link active" : "ms-link"}`}
                                                            to={process.env.PUBLIC_URL + "/" + data.routerLink[0]}
                                                            onClick={() => handleClick(process.env.PUBLIC_URL + "/" + data.routerLink[0])}
                                                        >
                                                            <span>{t(menuTranslationMapping[data?.name] || data?.name, data?.name)}</span>
                                                        </Link>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    }
                                </li>
                            );
                        })}
                        {/* Logout in Sidebar */}
                        <li className="collapsed">
                            <a href="#!" className="m-link" onClick={(e) => { e.preventDefault(); handleLogoutClick(); }}>
                                <span className="sidebar_icon">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                                        <path d="M2.81837 0H8.45512C9.20259 0 9.91946 0.299435 10.448 0.832433C10.9766 1.36543 11.2735 2.08833 11.2735 2.84211V6.63158H10.334V2.84211C10.334 2.33959 10.1361 1.85766 9.78371 1.50232C9.43134 1.14699 8.95343 0.947368 8.45512 0.947368H2.81837C2.32005 0.947368 1.84214 1.14699 1.48978 1.50232C1.13741 1.85766 0.939457 2.33959 0.939457 2.84211V15.1579C0.939457 15.6604 1.13741 16.1423 1.48978 16.4977C1.84214 16.853 2.32005 17.0526 2.81837 17.0526H8.45512C8.95343 17.0526 9.43134 16.853 9.78371 16.4977C10.1361 16.1423 10.334 15.6604 10.334 15.1579V11.3684H11.2735V15.1579C11.2735 15.9117 10.9766 16.6346 10.448 17.1676C9.91946 17.7006 9.20259 18 8.45512 18H2.81837C2.07089 18 1.35403 17.7006 0.825482 17.1676C0.296935 16.6346 0 15.9117 0 15.1579V2.84211C0 2.08833 0.296935 1.36543 0.825482 0.832433C1.35403 0.299435 2.07089 0 2.81837 0ZM5.63674 8.52632H16.2056L13.1524 5.44737L13.7724 4.73684L18 9L13.7724 13.2632L13.1524 12.5526L16.2056 9.47368H5.63674V8.52632Z" fill="white"/>
                                    </svg>
                                </span>
                                <span>{t('logout', 'Sign Out')}</span>
                            </a>
                        </li>
                    </ul>
                </div>
            </div>

            {/* Logout Confirmation Dialog */}
            <Dialog 
                open={logoutDialogOpen} 
                onClose={() => setLogoutDialogOpen(false)}
                PaperProps={{
                    sx: {
                        backgroundColor: '#252525',
                        border: '1px solid rgba(230, 210, 249, 0.1)',
                        borderRadius: '20px',
                        minWidth: '350px'
                    }
                }}
            >
                <DialogTitle sx={{ padding: '16px', fontSize: '18px', textAlign: 'center', color: '#fff' }}>
                    {t('confirm_logout', 'Confirm Logout')}
                </DialogTitle>
                <DialogContent sx={{ pb: 0 }}>
                    <Typography sx={{ padding: '0 16px 0', fontSize: '15px', textAlign: 'center', }}>
                        {t('logout_confirmation_message', 'Are you sure you want to logout?')}
                    </Typography>
                </DialogContent>
                <DialogActions 
                    sx={{
                        px: '16px',
                        py: '20px',
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        gap: '6px',
                    }}
                >
                    <Button 
                        onClick={() => setLogoutDialogOpen(false)} 
                        sx={{
                            backgroundColor: 'rgba(227, 214, 245, 0.2) !important',
                            border: '1px solid rgba(227, 214, 245, 0.1)',
                            fontSize: '15px',
                            lineHeight: '20px',
                            fontWeight: '400',
                            padding: '11px 20px',
                            minWidth: { xs: '100%', sm: '150px' },
                            color: 'white',
                            borderRadius: '999px',
                            textTransform: 'capitalize',
                        }}
                    >
                        {t('cancel', 'Cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleLogout}
                        sx={{
                            py: 1.5,
                            borderRadius: '999px',
                            fontWeight: '400',
                            fontSize: '15px',
                            lineHeight: '20px',
                            background:
                                'linear-gradient(90deg, rgba(161, 37, 235, 1) 0%, rgba(253, 86, 201, 1) 100%)',
                            textTransform: 'capitalize',
                            minWidth: { xs: '100%', sm: '150px' },
                            marginLeft: '10px',
                        }}
                    >
                        {t('logout', 'Logout')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default Sidebar;
